import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from '../user/user.service';
import { UserModel } from '../user/user.model';
import { SupportTicketsService } from './tickets.service';
import { SupportTicketsResponsesModel } from './ticketsResponses.model';
import { SUPPORT_PRIORITIES } from './tickets.component';
import { NotificationService } from '../../modules_v2/shared/services/notification.service';

interface SupportTicketDetail {
    id: number;
    name: string;
    subject: string;
    message: string;
    category: string;
    priority: number;
    resolved: boolean;
    date: string;
}

interface SupportTicketMessage {
    name: string;
    message: string;
    date: string;
}

@Component({
    selector: 'app-supportticketsconversation',
    templateUrl: './tickets-conversation.component.html',
    styleUrls: ['./tickets-dialogs.css'],
    standalone: false
})
export class SupportTicketsConversationComponent implements OnInit {
    @ViewChild('thread') thread?: ElementRef<HTMLElement>;

    user: UserModel;
    ticket: SupportTicketDetail | null = null;
    messages: SupportTicketMessage[] = [];
    reply = '';
    loading = true;
    loadError = false;
    sending = false;
    resolving = false;

    private changed = false;

    constructor(
        public dialogRef: MatDialogRef<SupportTicketsConversationComponent, boolean>,
        @Inject(MAT_DIALOG_DATA) public data: { ticketId: number },
        private supportTicketService: SupportTicketsService,
        private userService: UserService,
        private notification: NotificationService,
    ) { }

    get priority() {
        return SUPPORT_PRIORITIES.find(p => p.value === this.ticket?.priority) || SUPPORT_PRIORITIES[0];
    }

    ngOnInit() {
        Promise.all([
            this.userService.loadData(),
            this.supportTicketService.getTicket(this.data.ticketId),
            this.supportTicketService.getListTicketResponses(this.data.ticketId),
        ])
            .then(([user, ticket, responses]) => {
                this.user = user;
                this.ticket = ticket as unknown as SupportTicketDetail;
                this.messages = (responses || []) as unknown as SupportTicketMessage[];
                this.scrollToEnd();
            })
            .catch(() => { this.loadError = true; })
            .finally(() => { this.loading = false; });
    }

    isMine(name: string): boolean {
        return !!this.user && name === this.user.username;
    }

    isSupport(name: string): boolean {
        return !!this.ticket && name !== this.ticket.name;
    }

    initials(name: string): string {
        return (name || '?').trim().charAt(0).toUpperCase();
    }

    send() {
        const message = this.reply.trim();
        if (!message || this.sending || !this.ticket || !this.user) return;

        this.sending = true;
        const response = {
            TicketId: this.ticket.id,
            Name: this.user.username,
            OwnerUid: this.user.uid,
            Message: message,
        } as unknown as SupportTicketsResponsesModel;

        this.supportTicketService.addTicketResponse(response)
            .then(() => this.supportTicketService.getListTicketResponses(this.ticket.id))
            .then(responses => {
                this.messages = (responses || []) as unknown as SupportTicketMessage[];
                this.reply = '';
                this.changed = true;
                this.scrollToEnd();
            })
            .catch(() => this.notification.error('No se pudo enviar la respuesta'))
            .finally(() => { this.sending = false; });
    }

    resolveTicket() {
        if (!this.ticket || this.resolving) return;

        this.resolving = true;
        this.supportTicketService.resolveTicket(this.ticket.id)
            .then(() => {
                this.ticket.resolved = true;
                this.changed = true;
                this.notification.ok('Incidencia marcada como resuelta');
            })
            .catch(() => this.notification.error('No se pudo resolver la incidencia'))
            .finally(() => { this.resolving = false; });
    }

    onReplyKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            this.send();
        }
    }

    closeDialog() {
        this.dialogRef.close(this.changed);
    }

    trackByIndex(i: number) { return i; }

    private scrollToEnd() {
        setTimeout(() => {
            const el = this.thread?.nativeElement;
            if (el) el.scrollTop = el.scrollHeight;
        });
    }
}
